/**
 * `/forgot-password` and `/reset-password/:token` (spec M0-F3 §8.4).
 *
 * Forgot always shows the same confirmation panel, for a known and an unknown address alike —
 * the server answers `{ok:true}` either way and takes the same time (acceptance D1).
 */
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useTRPCClient } from '../../lib/trpc';
import { AuthError, AuthShell } from './AuthShell';
import { looksLikeServerInternals } from '../../app/errors';

export function ForgotPassword() {
  const client = useTRPCClient();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const request = useMutation({
    mutationFn: (input: { email: string }) => client.auth.requestPasswordReset.mutate(input),
    // Even a failure must not tell the caller whether the address exists.
    onSettled: () => setSent(true),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (request.isPending || email.trim() === '') return;
    request.mutate({ email: email.trim() });
  };

  return (
    <AuthShell title="Reset your password" testId="forgot-page">
      {sent ? (
        <p className="ez-auth__panel" data-testid="forgot-confirmation">
          If that address belongs to an account, a reset link is on its way.
        </p>
      ) : (
        <form className="ez-auth__form" onSubmit={submit} noValidate>
          <div>
            <label className="ez-auth__label" htmlFor="forgot-email">
              Your email address
            </label>
            <input
              id="forgot-email"
              className="ez-auth__input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              data-testid="forgot-email"
            />
          </div>
          <button
            type="submit"
            className="ez-auth__button"
            disabled={request.isPending || email.trim() === ''}
            data-testid="forgot-submit"
          >
            Send reset link
          </button>
        </form>
      )}
      <p className="ez-auth__links">
        <a className="ez-auth__link" href="/login" data-testid="forgot-back">
          ← Back to log in
        </a>
      </p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const client = useTRPCClient();
  const { token = '' } = useParams();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const reset = useMutation({
    mutationFn: (input: { token: string; password: string }) => client.auth.resetPassword.mutate(input),
    onSuccess: () => window.location.assign('/?toast=password-updated'),
    onError: (caught: unknown) => {
      const shape = caught as { data?: { appCode?: string }; message?: string };
      if (shape.data?.appCode === 'RESET_TOKEN_INVALID') {
        setExpired(true);
        return;
      }
      /*
       * P-049: the api's sentence only when it is one. A server fault now arrives as a generic
       * message with an error id; anything that still looks like a statement or a stack is
       * dropped here as well, so this page can never print the database again.
       */
      const message = shape.message?.trim() ?? '';
      setError(
        message !== '' && !looksLikeServerInternals(message)
          ? message
          : 'Could not set that password.',
      );
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (reset.isPending) return;
    if (password !== confirmation) {
      setError('The two passwords do not match.');
      return;
    }
    setError(null);
    reset.mutate({ token, password });
  };

  if (expired) {
    return (
      <AuthShell title="Reset your password" testId="reset-page">
        <p className="ez-auth__panel" data-testid="reset-expired">
          This link has expired.
        </p>
        <p className="ez-auth__links">
          <a className="ez-auth__link" href="/forgot-password">
            Request a new one
          </a>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" testId="reset-page">
      <form className="ez-auth__form" onSubmit={submit} noValidate>
        <div>
          <label className="ez-auth__label" htmlFor="reset-password">
            New password
          </label>
          <input
            id="reset-password"
            className="ez-auth__input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            data-testid="reset-password"
          />
          <p className="ez-auth__hint">At least 12 characters. Avoid your email address.</p>
        </div>
        <div>
          <label className="ez-auth__label" htmlFor="reset-confirm">
            Confirm password
          </label>
          <input
            id="reset-confirm"
            className="ez-auth__input"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            data-testid="reset-confirm"
          />
        </div>
        <AuthError>{error}</AuthError>
        <button
          type="submit"
          className="ez-auth__button"
          disabled={reset.isPending || password.length === 0}
          data-testid="reset-submit"
        >
          Set password
        </button>
      </form>
    </AuthShell>
  );
}
